import { useEffect, useMemo, useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { authenticatedFetch } from "../../services/apiClient";
import oldLineSolLogo from "../home/OLDlogolong.png";
import styles from "./crypto.module.css";

const MIN_WITHDRAWAL_AMOUNT = 1;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOLSCAN_TX_URL = "https://solscan.io/tx";

function formatBalance(value) {
  const number = Number(value || 0);
  return number.toFixed(2);
}

export default function CryptoPortal() {
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [solanaWalletAddress, setSolanaWalletAddress] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTransactionUrl, setStatusTransactionUrl] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [balance, setBalance] = useState(0);
  const [hasParticipantRecord, setHasParticipantRecord] = useState(false);
  const [isBalanceLoading, setIsBalanceLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthUser(user);
      setLoginError("");
      setSubmitError("");
      setStatusMessage("");
      setStatusTransactionUrl("");
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUser) {
      setBalance(0);
      setHasParticipantRecord(false);
      setIsBalanceLoading(false);
      return undefined;
    }

    setIsBalanceLoading(true);
    const userRef = doc(db, "users", authUser.uid);

    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        const data = snapshot.data() || {};
        setHasParticipantRecord(snapshot.exists());
        setBalance(Number(data.balance || 0));
        setIsBalanceLoading(false);
      },
      (error) => {
        console.error("Failed to load participant balance:", error);
        setHasParticipantRecord(false);
        setBalance(0);
        setIsBalanceLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authUser]);

  const parsedAmount = useMemo(() => Number(withdrawalAmount), [withdrawalAmount]);
  const canSubmit = Boolean(
    authUser
      && hasParticipantRecord
      && !isSubmitting
      && solanaWalletAddress.trim()
      && Number.isFinite(parsedAmount)
      && parsedAmount >= MIN_WITHDRAWAL_AMOUNT
  );

  const handleLogin = async (event) => {
    event.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const trimmedUsername = username.trim();
      if (!trimmedUsername) {
        setLoginError("Please enter your username.");
        return;
      }

      if (!password) {
        setLoginError("Please enter your password.");
        return;
      }

      const pseudoEmail = `${trimmedUsername}@pseudonym.local`;
      const response = await signInWithEmailAndPassword(auth, pseudoEmail, password);

      await setDoc(
        doc(db, "users", response.user.uid),
        { lastActive: new Date().toISOString() },
        { merge: true }
      );
    } catch (error) {
      console.error("Participant login failed:", error);

      if (error.code === "auth/invalid-credential") {
        setLoginError("Invalid username or password.");
      } else if (error.code === "auth/too-many-requests") {
        setLoginError("Too many failed attempts. Try again later.");
      } else {
        setLoginError("Login failed. Please try again.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleWithdraw = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setStatusMessage("");
    setStatusTransactionUrl("");

    if (!authUser) {
      setSubmitError("Please log in first.");
      return;
    }

    if (!hasParticipantRecord) {
      setSubmitError("No participant balance record was found for this account.");
      return;
    }

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setSubmitError("Enter a valid withdrawal amount.");
      return;
    }

    if (parsedAmount < MIN_WITHDRAWAL_AMOUNT) {
      setSubmitError(
        `Minimum withdrawal is $${formatBalance(MIN_WITHDRAWAL_AMOUNT)} because Coinbase requires at least 1 USDC per send.`
      );
      return;
    }

    if (parsedAmount > balance) {
      setSubmitError(`Amount exceeds available balance of $${formatBalance(balance)}.`);
      return;
    }

    if (!BACKEND_URL) {
      setSubmitError("Backend URL is not configured.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authenticatedFetch(`${BACKEND_URL}/api/crypto/withdraw`, {
        method: "POST",
        body: JSON.stringify({
        withdrawalAmount: withdrawalAmount.trim(),
        solanaWalletAddress: solanaWalletAddress.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result?.success) {
        throw new Error(result?.message || "Withdrawal failed. Please try again.");
      }

      const responseData = result.data || {};
      setStatusMessage(responseData.message || result.message || "Withdrawal request submitted successfully.");
      setStatusTransactionUrl(
        responseData.transactionId ? `${SOLSCAN_TX_URL}/${responseData.transactionId}` : ""
      );
      setWithdrawalAmount("");
      setSolanaWalletAddress("");
    } catch (error) {
      console.error("Withdrawal failed:", error);
      const message = error?.message || "Withdrawal failed. Please try again.";
      setSubmitError(String(message).replace(/^internal:\s*/i, ""));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setSubmitError("");
    setStatusMessage("");
    setStatusTransactionUrl("");
    await signOut(auth);
  };

  if (!authUser) {
    return (
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={styles.copyBlock}>
            <img className={styles.logo} src={oldLineSolLogo} alt="LineSol" />
            <span className={`${styles.eyebrow} ${styles.panelEyebrow}`}>Participant Access Only</span>
            <h1 className={styles.title}>Solana Withdrawals</h1>
            <p className={styles.subtitle}>
              View your balance and withdraw your earnings in USDC on Solana. 
              Specify the amount you want to withdraw and your Solana wallet address to receive your funds.
            </p>
          </div>

          <form className={styles.card} onSubmit={handleLogin}>
            <h2 className={styles.cardTitle}>Login</h2>
            <p className={styles.cardText}>
              Use the same login that you use for the mobile app. You can find your username in the app under Login Details. 
            </p>

            <label className={styles.fieldLabel}>
              Username
              <input
                className={styles.input}
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                placeholder="Enter your username"
                autoComplete="username"
              />
            </label>

            <label className={styles.fieldLabel}>
              Password
              <input
                className={styles.input}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </label>

            {loginError ? <div className={styles.errorBanner}>{loginError}</div> : null}

            <button className={styles.primaryButton} type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? "Signing In..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.singlePanelShell}>
        <form className={styles.card} onSubmit={handleWithdraw}>
          <img className={styles.logo} src={oldLineSolLogo} alt="LineSol" />
          
          <h1 className={styles.cardTitle}>Solana Withdrawal</h1>

          <p className={styles.balanceSummary}>
            Available balance: {isBalanceLoading ? "Loading..." : `$${formatBalance(balance)}`}
          </p>
          <p className={styles.cardText}>
            Enter the amount you want to withdraw and your Solana wallet address for USDC.
            The minimum withdrawal amount is $1.00.
          </p>

          {!hasParticipantRecord && !isBalanceLoading ? (
            <div className={styles.warningBanner}>
              This account authenticated successfully, but no participant record was found in users/{authUser.uid}.
            </div>
          ) : null}

          <label className={styles.fieldLabel}>
            Withdrawal Amount (USDC)
            <input
              className={styles.input}
              type="number"
              min="1"
              step="0.01"
              inputMode="decimal"
              value={withdrawalAmount}
              onChange={(event) => setWithdrawalAmount(event.target.value)}
              placeholder="1.00"
            />
          </label>

          <label className={styles.fieldLabel}>
            Solana Wallet Address
            <textarea
              className={styles.textarea}
              value={solanaWalletAddress}
              onChange={(event) => setSolanaWalletAddress(event.target.value)}
              placeholder="Enter your Solana wallet address for receiving USDC."
              rows={4}
            />
          </label>

          {submitError ? <div className={styles.errorBanner}>{submitError}</div> : null}
          {statusMessage ? (
            <div className={styles.successBanner}>
              <div>{statusMessage}</div>
              {statusTransactionUrl ? (
                <a href={statusTransactionUrl} target="_blank" rel="noreferrer" className={styles.transactionLink}>
                  View the transaction on Solscan
                </a>
              ) : null}
            </div>
          ) : null}

          <button className={styles.primaryButton} type="submit" disabled={!canSubmit}>
            {isSubmitting ? "Withdrawing..." : "Withdraw"}
          </button>
          <button className={`${styles.secondaryButton} ${styles.fullWidthButton}`} type="button" onClick={handleLogout}>
            Logout
          </button>
        </form>
      </div>
    </div>
  );
}